import { useState } from 'react';
import { AddressSuggestion } from '@/types/customer';

export const useAddressSearch = () => {
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [selectedStreet, setSelectedStreet] = useState<{
    street: string;
    city: string;
    state: string;
    postcode: string;
  } | null>(null);
  const [searchStep, setSearchStep] = useState<'street' | 'houseNumber'>('street');

  // Enhanced 2-step geocoding function
  const searchStreets = async (query: string) => {
    if (!query.trim() || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    setIsLoadingAddress(true);
    try {
      const searchQuery = `${query}, Deutschland`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&addressdetails=1&limit=15&countrycodes=de&featuretype=way`
      );
      
      if (response.ok) {
        const results = await response.json();
        
        // Group streets by state and city
        const streetMap = new Map();
        
        for (const result of results) {
          const address = result.address;
          const street = address?.road;
          const city = address?.city || address?.town || address?.village || address?.hamlet || address?.municipality || address?.suburb || address?.neighbourhood || '';
          const state = address?.state || '';
          const postcode = address?.postcode || '';
          
          if (street && city && state) {
            const key = `${street}-${city}-${state}`;
            if (!streetMap.has(key)) {
              streetMap.set(key, {
                street,
                city,
                state,
                postcode,
                displayName: `${street}, ${postcode ? postcode + ' ' : ''}${city}, ${state}`,
                fullAddress: `${street}, ${postcode ? postcode + ' ' : ''}${city}, ${state}, Deutschland`,
                isStreet: true
              });
            }
          }
        }
        
        const suggestions = Array.from(streetMap.values());
        setAddressSuggestions(suggestions);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Straßen:', error);
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const searchHouseNumbers = async (street: string, city: string, state: string, postcode: string) => {
    setIsLoadingAddress(true);
    try {
      // Search for all addresses on this street to get real house numbers
      const streetQuery = `${street}, ${city}, ${state}, Deutschland`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(streetQuery)}&format=json&addressdetails=1&limit=100&countrycodes=de`
      );
      
      if (response.ok) {
        const results = await response.json();
        
        // Extract unique house numbers for this exact street
        const houseNumbers = new Set();
        const suggestions = [];
        
        for (const result of results) {
          const address = result.address;
          const resultStreet = address?.road;
          const resultCity = address?.city || address?.town || address?.village || address?.hamlet || address?.municipality || address?.suburb || address?.neighbourhood || '';
          const houseNumber = address?.house_number;
          
          // More flexible matching - check if street name is contained
          if (houseNumber && 
              resultStreet && resultStreet.toLowerCase().includes(street.toLowerCase()) &&
              resultCity && resultCity.toLowerCase() === city.toLowerCase() && 
              !houseNumbers.has(houseNumber)) {
            houseNumbers.add(houseNumber);
            const fullAddress = `${street} ${houseNumber}, ${postcode ? postcode + ' ' : ''}${city}, ${state}, Deutschland`;
            suggestions.push({
              displayName: houseNumber,
              fullAddress: fullAddress,
              street,
              city,
              state,
              postcode,
              isHouseNumber: true
            });
          }
        }
        
        // If no real house numbers found, create common suggestions as fallback
        if (suggestions.length === 0) {
          for (let i = 1; i <= 50; i++) {
            suggestions.push({
              displayName: i.toString(),
              fullAddress: `${street} ${i}, ${postcode ? postcode + ' ' : ''}${city}, ${state}, Deutschland`,
              street,
              city,
              state,
              postcode,
              isHouseNumber: true
            });
          }
        }
        
        // Sort all suggestions intelligently
        suggestions.sort((a, b) => {
          const aNum = a.displayName;
          const bNum = b.displayName;
          
          // Extract main number and suffix for proper sorting
          const parseHouseNumber = (num: string) => {
            const match = num.match(/^(\d+)(.*)$/);
            if (match) {
              return {
                main: parseInt(match[1]),
                suffix: match[2] || ''
              };
            }
            return { main: 0, suffix: num };
          };
          
          const aParsed = parseHouseNumber(aNum);
          const bParsed = parseHouseNumber(bNum);
          
          // First sort by main number
          if (aParsed.main !== bParsed.main) {
            return aParsed.main - bParsed.main;
          }
          
          // Then sort by suffix
          return aParsed.suffix.localeCompare(bParsed.suffix);
        });
        
        // Add option to continue without house number at the beginning
        suggestions.unshift({
          displayName: "Ohne Hausnummer fortfahren",
          fullAddress: `${street}, ${postcode ? postcode + ' ' : ''}${city}, ${state}, Deutschland`,
          street,
          city,
          state,
          postcode,
          isHouseNumber: false
        });
        
        setAddressSuggestions(suggestions);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Hausnummern:', error);
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const selectAddressSuggestion = (suggestion: AddressSuggestion, onAddressChange: (address: string) => void, inputRef?: React.RefObject<HTMLInputElement>) => {
    if (suggestion.isStreet) {
      // User selected a street, now prepare for house number input
      setSelectedStreet({
        street: suggestion.street!,
        city: suggestion.city!,
        state: suggestion.state!,
        postcode: suggestion.postcode!
      });
      setSearchStep('houseNumber');
      
      // Set address to "Street " (with space) and focus input for house number entry
      const addressWithSpace = `${suggestion.street} `;
      onAddressChange(addressWithSpace);
      setAddressSuggestions([]);
      
      // Focus input and position cursor at the end
      setTimeout(() => {
        if (inputRef?.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(addressWithSpace.length, addressWithSpace.length);
        }
      }, 0);
    } else {
      // User selected a house number or chose to continue without one
      onAddressChange(suggestion.fullAddress);
      setAddressSuggestions([]);
      setSelectedStreet(null);
      setSearchStep('street');
    }
  };

  const resetAddressSearch = (onAddressChange: (address: string) => void) => {
    setSelectedStreet(null);
    setSearchStep('street');
    setAddressSuggestions([]);
    onAddressChange('');
  };

  return {
    isLoadingAddress,
    addressSuggestions,
    selectedStreet,
    searchStep,
    searchStreets,
    searchHouseNumbers,
    selectAddressSuggestion,
    resetAddressSearch,
    setAddressSuggestions,
    setSelectedStreet,
    setSearchStep
  };
};